import axios from 'axios';
import type { AxiosInstance } from 'axios';
import type {
  Plea,
  PleaPayload,
  AttachmentUploadUrlRequest,
  AttachmentUploadUrlResponse,
  ConfirmAttachmentRequest,
  Attachment,
} from '../types';

export const pleasService = {
  getPleas: (api: AxiosInstance, agreementUuid: string): Promise<Plea[]> =>
    api.get<Plea[]>(`/agreements/${agreementUuid}/pleas`).then((res) => res.data),

  createPlea: (api: AxiosInstance, agreementUuid: string, payload: PleaPayload): Promise<Plea> =>
    api.post<Plea>(`/agreements/${agreementUuid}/pleas`, payload).then((res) => res.data),

  updatePlea: (api: AxiosInstance, agreementUuid: string, pleaId: string, payload: PleaPayload): Promise<void> =>
    api.patch(`/agreements/${agreementUuid}/pleas/${pleaId}`, payload).then(() => undefined),

  deletePlea: (api: AxiosInstance, agreementUuid: string, pleaId: string): Promise<void> =>
    api.delete(`/agreements/${agreementUuid}/pleas/${pleaId}`).then(() => undefined),

  requestAttachmentUploadUrl: (
    api: AxiosInstance,
    agreementUuid: string,
    pleaUuid: string,
    request: AttachmentUploadUrlRequest,
  ): Promise<AttachmentUploadUrlResponse> =>
    api
      .post<AttachmentUploadUrlResponse>(
        `/agreements/${agreementUuid}/pleas/${pleaUuid}/attachments/upload-url`,
        request,
      )
      .then((res) => res.data),

  uploadToSignedUrl: (url: string, contentType: string, file: File): Promise<void> =>
    axios.put(url, file, { headers: { 'Content-Type': contentType } }).then(() => undefined),

  confirmAttachment: (
    api: AxiosInstance,
    agreementUuid: string,
    pleaUuid: string,
    request: ConfirmAttachmentRequest,
  ): Promise<Attachment> =>
    api
      .post<Attachment>(`/agreements/${agreementUuid}/pleas/${pleaUuid}/attachments`, request)
      .then((res) => res.data),

  getAttachmentNames: (
    api: AxiosInstance,
    agreementUuid: string,
    pleaIds: string[],
  ): Promise<Record<string, string[]>> =>
    api
      .get<Record<string, string[]>>(`/agreements/${agreementUuid}/pleas/attachments`, {
        params: { pleaIds },
      })
      .then((res) => res.data),

  /** Fetches the full attachment list (including scan status) for a single plea. */
  getPleaAttachments: (api: AxiosInstance, agreementUuid: string, pleaUuid: string): Promise<Attachment[]> =>
    api
      .get<Attachment[]>(`/agreements/${agreementUuid}/pleas/${pleaUuid}/attachments`)
      .then((res) => res.data),
};
